import { calcAPCA } from 'apca-w3'

type HexColor = string

const BRAND_GREEN = '#1EC95B'

const contrastValue = (fgColor: HexColor, bgColor: HexColor) =>
  Math.abs(calcAPCA(fgColor, bgColor) as number)

/**
 * @param color a color in hex format
 * @param transparency accepts either a number between 0 and 255 or a string between 0% and 100%
 */
const getTransparentColor = (
  color: HexColor,
  transparency: number | string
) => {
  let t
  if (typeof transparency === 'string') {
    if (transparency.endsWith('%')) {
      t = +transparency.slice(0, -1)
    } else {
      t = +transparency
    }
    t *= 2.55
  } else {
    t = transparency
  }
  t = Math.min(255, Math.max(0, Math.round(t)))

  return `${color}${t.toString(16).padStart(2, '0')}`
}

const colorsMap = new Map<string, ContrastColor>()

class ContrastColor {
  /** The color in a string format */
  color: HexColor

  /** The color expected to be behind this one.
   * (Used for calculating contrast ratios of transparent colors)
   */
  base: HexColor

  /**
   * Black or white, whichever contrasts most with this color. Used for all text and icons on it.
   */
  highContrast!: ContrastColor

  /**
   * Used to get a transparent version of this color.
   * Accepts either a number between 0 and 255 or a string between 0% and 100%.
   */
  transparent!: (amount: number | string) => string;

  [Symbol.toPrimitive](hint: string) {
    switch (hint) {
      case 'string':
      case 'default':
        return this.color
      case 'number':
        return NaN
      default:
        return null
    }
  }

  get key() {
    return ContrastColor.toKey(this.color, this.base)
  }

  static toKey(color: HexColor, base: HexColor) {
    return `${color}-${base}`.toUpperCase()
  }

  /** Returns the cached instance for this color/base pair, creating it on first use. */
  static of(color: HexColor, base: HexColor = '#FFFFFF') {
    return (
      colorsMap.get(ContrastColor.toKey(color, base)) ??
      new ContrastColor(color, base)
    )
  }

  private constructor(color: HexColor, base: HexColor) {
    this.base = base
    this.color = color
    colorsMap.set(this.key, this)

    const bestHighContrast =
      color.toUpperCase() === BRAND_GREEN ||
      contrastValue('#FFFFFF', color) > contrastValue('#000000', color)
        ? '#FFFFFF'
        : '#000000'

    this.highContrast = ContrastColor.of(bestHighContrast, this.color)

    this.transparent = (amount) => getTransparentColor(color, amount)
  }

  toString() {
    return this.color
  }
}

export { ContrastColor, contrastValue }
export type { HexColor }
